'use client';

import React, {
  createContext,
  useCallback,
  useContext,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';
import { useDensity } from '@liftango/ui-tokens';
import { useAutocomplete } from './useAutocomplete';
import { useAutocompleteQuery } from './useAutocompleteQuery';
import type { GetOptionKey, GetOptionLabel, GetOptionDisabled, LoadOptions } from './types';

export interface AutocompleteProps<T> {
  /** Currently selected value(s). `T | null` (single), `T[]` (multi); widens to `string` / `(T | string)[]` with `allowFreeText`. */
  value: T | T[] | string | (T | string)[] | null;
  /** Fired with the next value when the user selects, removes, or commits free text. */
  onChange: (next: T | T[] | string | (T | string)[] | null) => void;
  /** In-memory option list. Mutually exclusive with `loadOptions`. */
  items?: T[];
  /** Async option loader. Receives the query and an AbortSignal. Mutually exclusive with `items`. */
  loadOptions?: LoadOptions<T>;
  /** Debounce window for `loadOptions` calls. Default 250ms. */
  debounceMs?: number;
  /** Minimum query length before `loadOptions` is called. Default 0. */
  minQueryLength?: number;
  /** Multi-select mode. `value` becomes `T[]`; selected items render as chips. */
  multiple?: boolean;
  /** Allow `Enter` on a non-matching query to commit as a string. */
  allowFreeText?: boolean;
  /** When true, the field is read-only and edits are refused; `onUnlock` fires on attempted edits. */
  locked?: boolean;
  /** Called when a user attempts to edit a locked field. Flip `locked` to false to allow. */
  onUnlock?: () => void;
  /** Greys out and disables the entire control. */
  disabled?: boolean;
  placeholder?: string;
  /** Required: stable string key per option. Used for React keys and selection equality. */
  getOptionKey: GetOptionKey<T>;
  /** Text shown in the input/chip after selection. Defaults to `String(option)`. */
  getOptionLabel?: GetOptionLabel<T>;
  /** Renders the dropdown row. Defaults to `getOptionLabel`. */
  renderOption?: (option: T) => React.ReactNode;
  /** Whether the option is disabled (skipped by keyboard, refused by select). */
  getOptionDisabled?: GetOptionDisabled<T>;
  emptyState?: React.ReactNode;
  loadingState?: React.ReactNode;
  className?: string;
  children?: React.ReactNode;
}

interface AutocompleteContextValue<T> {
  hook: ReturnType<typeof useAutocomplete<T>>;
  visibleItems: T[];
  value: T | T[] | string | (T | string)[] | null;
  removeAt: (index: number) => void;
  getOptionKey: GetOptionKey<T>;
  getOptionLabel: (o: T) => string;
  renderOption: (o: T) => React.ReactNode;
  getOptionDisabled: (o: T) => boolean;
  listboxId: string;
  inputRef: React.RefObject<HTMLInputElement>;
  locked: boolean;
  disabled: boolean;
  multiple: boolean;
  allowFreeText: boolean;
  isLoading: boolean;
}

const Ctx = createContext<AutocompleteContextValue<unknown> | null>(null);

function useCtx<T>(): AutocompleteContextValue<T> {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('Autocomplete subcomponents must be used inside <Autocomplete>');
  return ctx as AutocompleteContextValue<T>;
}

const Root = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: ${(p) => p.theme.spaceAlias.xs};
`;

const InputBox = styled.div<{ $locked: boolean; $disabled: boolean; $height: number }>`
  display: flex;
  align-items: center;
  gap: ${(p) => p.theme.spaceAlias.xs};
  height: ${(p) => p.$height}px;
  border: 1px solid ${(p) => p.theme.colors.neutral.line};
  border-radius: ${(p) => p.theme.radius.md};
  padding: 0 ${(p) => p.theme.spaceAlias.md};
  background: ${(p) =>
    p.$disabled || p.$locked ? p.theme.colors.neutral.panel : p.theme.colors.neutral.bg};
  cursor: ${(p) => (p.$disabled ? 'not-allowed' : p.$locked ? 'default' : 'text')};
  &:focus-within {
    border-color: ${(p) => p.theme.colors.brand.primary};
    outline: ${(p) => p.theme.focus.width} solid ${(p) => p.theme.colors.brand.primary};
    outline-offset: ${(p) => p.theme.focus.insetOffset};
  }
`;

const NativeInput = styled.input`
  border: none;
  background: transparent;
  outline: none;
  font-size: ${(p) => p.theme.fontSize.body};
  font-family: ${(p) => p.theme.fontFamily.ui};
  color: ${(p) => p.theme.colors.neutral.ink};
  flex: 1;
  padding: 0;
  min-width: 0;
  &::placeholder {
    color: ${(p) => p.theme.colors.neutral.inkFaint};
  }
`;

const Listbox = styled.ul`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  z-index: 100;
  margin: 0;
  padding: ${(p) => p.theme.spaceAlias.xs} 0;
  list-style: none;
  background: ${(p) => p.theme.colors.neutral.panel};
  border: 1px solid ${(p) => p.theme.colors.neutral.line};
  border-radius: ${(p) => p.theme.radius.md};
  box-shadow: ${(p) => p.theme.elevation.overlay.shadow};
  max-height: 280px;
  overflow-y: auto;
`;

const OptionItem = styled.li<{ $active: boolean; $selected: boolean; $disabled: boolean }>`
  padding: ${(p) => p.theme.spaceAlias.sm} ${(p) => p.theme.spaceAlias.md};
  cursor: ${(p) => (p.$disabled ? 'not-allowed' : 'pointer')};
  background: ${(p) => (p.$active ? p.theme.colors.neutral.panelAlt : 'transparent')};
  color: ${(p) =>
    p.$disabled
      ? p.theme.colors.neutral.inkFaint
      : p.$selected
        ? p.theme.colors.brand.primary
        : p.theme.colors.neutral.ink};
  font-weight: ${(p) => (p.$selected ? p.theme.fontWeight.semibold : p.theme.fontWeight.regular)};
  font-family: ${(p) => p.theme.fontFamily.ui};
  font-size: ${(p) => p.theme.fontSize.body};
`;

const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  align-items: center;
`;

const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px ${(p) => p.theme.spaceAlias.sm};
  border-radius: ${(p) => p.theme.radius.pill};
  background: ${(p) => p.theme.colors.neutral.panel};
  border: 1px solid ${(p) => p.theme.colors.neutral.line};
  font-family: ${(p) => p.theme.fontFamily.ui};
  font-size: ${(p) => p.theme.fontSize.sm};
  color: ${(p) => p.theme.colors.neutral.ink};
`;

const ChipRemove = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  color: ${(p) => p.theme.colors.neutral.inkMute};
  font-size: 14px;
  line-height: 1;
  &:hover {
    color: ${(p) => p.theme.colors.neutral.ink};
  }
`;

const ClearButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0 4px;
  color: ${(p) => p.theme.colors.neutral.inkMute};
  font-size: 16px;
  line-height: 1;
  &:hover {
    color: ${(p) => p.theme.colors.neutral.ink};
  }
`;

const StaticRow = styled.li`
  padding: ${(p) => p.theme.spaceAlias.sm} ${(p) => p.theme.spaceAlias.md};
  text-align: center;
  color: ${(p) => p.theme.colors.neutral.inkMute};
  font-family: ${(p) => p.theme.fontFamily.ui};
  font-size: ${(p) => p.theme.fontSize.sm};
  list-style: none;
`;

/**
 * Domain-agnostic combobox. Accepts either an in-memory `items` array
 * (filter-as-you-type) or an async `loadOptions` function (debounced
 * remote fetch). Use shorthand props for the 80% case, or pass children
 * (`<Autocomplete.Input>` etc.) for full control.
 */
export function Autocomplete<T>(props: AutocompleteProps<T>) {
  const {
    value,
    onChange,
    items,
    multiple = false,
    allowFreeText = false,
    locked = false,
    onUnlock,
    disabled = false,
    placeholder,
    getOptionKey,
    getOptionLabel = (o: T) => String(o),
    renderOption,
    getOptionDisabled = () => false,
    children,
    className,
  } = props;

  if (process.env.NODE_ENV !== 'production') {
    if (children && (items || props.loadOptions)) {
      throw new Error(
        'Autocomplete: pass either children (compound mode) or items/loadOptions (shorthand mode), not both.',
      );
    }
    if (items && props.loadOptions) {
      throw new Error('Autocomplete: items and loadOptions are mutually exclusive.');
    }
  }

  const [query, setQuery] = useState('');

  const isRemote = !!props.loadOptions;
  const remoteQuery = useAutocompleteQuery<T>({
    query,
    loadOptions: props.loadOptions ?? (async () => [] as T[]),
    debounceMs: props.debounceMs,
    minQueryLength: props.minQueryLength,
    enabled: isRemote && !locked && !disabled,
  });

  const filteredItems = useMemo(() => {
    if (isRemote) return remoteQuery.items;
    if (!items) return [] as T[];
    if (query.length === 0) return items;
    const q = query.toLowerCase();
    return items.filter((i) => getOptionLabel(i).toLowerCase().includes(q));
  }, [isRemote, remoteQuery.items, items, query, getOptionLabel]);

  const isLoading = isRemote && remoteQuery.loading;

  const hook = useAutocomplete<T>({
    value,
    onChange,
    items: filteredItems,
    multiple,
    allowFreeText,
    locked,
    onUnlock,
    getOptionKey,
    getOptionLabel,
    getOptionDisabled,
    query,
    onQueryChange: setQuery,
  });

  const removeAt = useCallback(
    (index: number) => {
      if (locked) {
        onUnlock?.();
        return;
      }
      const arr = (Array.isArray(value) ? value : []) as (T | string)[];
      onChange(arr.filter((_, i) => i !== index));
    },
    [value, onChange, locked, onUnlock],
  );

  const inputRef = useRef<HTMLInputElement>(null);
  const listboxId = useId();

  const ctx: AutocompleteContextValue<T> = {
    hook,
    visibleItems: filteredItems,
    value,
    removeAt,
    getOptionKey,
    getOptionLabel,
    renderOption: renderOption ?? ((o: T) => getOptionLabel(o)),
    getOptionDisabled,
    listboxId,
    inputRef,
    locked,
    disabled,
    multiple,
    allowFreeText,
    isLoading,
  };

  return (
    <Ctx.Provider value={ctx as AutocompleteContextValue<unknown>}>
      <Root className={className}>
        {children ?? (
          <>
            {multiple && <Chips />}
            <Input placeholder={placeholder} />
            <List />
          </>
        )}
      </Root>
    </Ctx.Provider>
  );
}

export interface AutocompleteInputProps {
  placeholder?: string;
}

/** Combobox text input. ARIA-wired; wraps focus + keyboard handlers. */
function Input({ placeholder }: AutocompleteInputProps) {
  const ctx = useCtx<unknown>();
  const { hook, listboxId, inputRef, locked, disabled, getOptionKey, visibleItems, allowFreeText } =
    ctx;
  const density = useDensity();

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (disabled) return;
    if (locked) return;
    if (e.key === 'Backspace' && hook.query.length === 0 && ctx.multiple) {
      const arr = (Array.isArray(ctx.value) ? ctx.value : []) as unknown[];
      if (arr.length > 0) {
        e.preventDefault();
        ctx.removeAt(arr.length - 1);
      }
      return;
    }
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (!hook.isOpen) hook.open();
        else hook.moveActive(1);
        break;
      case 'ArrowUp':
        e.preventDefault();
        hook.moveActive(-1);
        break;
      case 'Home':
        e.preventDefault();
        hook.moveActive('first');
        break;
      case 'End':
        e.preventDefault();
        hook.moveActive('last');
        break;
      case 'Enter':
        e.preventDefault();
        if (hook.isOpen && visibleItems[hook.activeIndex]) hook.commitActive();
        else if (allowFreeText) hook.commitFreeText();
        break;
      case 'Escape':
        e.preventDefault();
        hook.close();
        break;
    }
  };

  const activeItem = visibleItems[hook.activeIndex];

  return (
    <InputBox $locked={locked} $disabled={disabled} $height={density.controlHeight}>
      <NativeInput
        ref={inputRef}
        role="combobox"
        aria-expanded={hook.isOpen}
        aria-controls={listboxId}
        aria-activedescendant={
          hook.isOpen && activeItem ? `${listboxId}-${getOptionKey(activeItem)}` : undefined
        }
        aria-readonly={locked || undefined}
        readOnly={locked}
        disabled={disabled}
        value={hook.query}
        onChange={(e) => hook.setQuery(e.target.value)}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
      />
      <Clear />
    </InputBox>
  );
}

/** Renders the multi-select chip row above the input. No-op in single mode or with empty value. */
function Chips() {
  const ctx = useCtx<unknown>();
  const { multiple, locked, value, getOptionKey, getOptionLabel, hook, removeAt } = ctx;
  if (!multiple) return null;
  const arr = (Array.isArray(value) ? value : []) as unknown[];
  if (arr.length === 0) return null;
  return (
    <ChipRow>
      {arr.map((v, i) => {
        const isStr = typeof v === 'string';
        const label = isStr ? (v as string) : getOptionLabel(v);
        const key = isStr ? `__free__${i}` : getOptionKey(v);
        return (
          <Chip key={key}>
            {label}
            {!locked && (
              <ChipRemove
                type="button"
                aria-label={`Remove ${label}`}
                onMouseDown={(e) => {
                  e.preventDefault();
                  if (isStr) removeAt(i);
                  else hook.remove(v);
                }}
              >
                ×
              </ChipRemove>
            )}
          </Chip>
        );
      })}
    </ChipRow>
  );
}

/** Inline × button that clears the query. Hidden when query is empty, locked, or disabled. */
function Clear() {
  const ctx = useCtx<unknown>();
  const { hook, locked, disabled, inputRef } = ctx;
  if (locked || disabled) return null;
  if (hook.query.length === 0) return null;
  return (
    <ClearButton
      type="button"
      aria-label="Clear"
      onMouseDown={(e) => {
        e.preventDefault();
        hook.setQuery('');
        inputRef.current?.focus();
      }}
    >
      ×
    </ClearButton>
  );
}

/** Empty-state row inside the listbox. */
function Empty({ children }: { children?: React.ReactNode }) {
  return <StaticRow role="presentation">{children ?? 'No matches'}</StaticRow>;
}

/** Loading-state row inside the listbox. */
function Loading({ children }: { children?: React.ReactNode }) {
  return <StaticRow role="presentation">{children ?? 'Loading…'}</StaticRow>;
}

/** Listbox dropdown. Renders visible items as `<Autocomplete.Option>`s in shorthand mode, or arbitrary children in compound mode. */
function List({ children }: { children?: React.ReactNode }) {
  const ctx = useCtx<unknown>();
  const {
    hook,
    visibleItems,
    listboxId,
    isLoading,
    getOptionKey,
    getOptionDisabled,
    renderOption,
  } = ctx;
  if (!hook.isOpen) return null;
  if (children !== undefined) {
    return (
      <Listbox id={listboxId} role="listbox">
        {children}
      </Listbox>
    );
  }
  return (
    <Listbox id={listboxId} role="listbox">
      {isLoading && visibleItems.length === 0 ? (
        <Loading />
      ) : visibleItems.length === 0 ? (
        <Empty />
      ) : (
        visibleItems.map((item, i) => {
          const key = getOptionKey(item);
          const optDisabled = getOptionDisabled(item);
          return (
            <OptionItem
              key={key}
              id={`${listboxId}-${key}`}
              role="option"
              aria-selected={hook.isSelected(item)}
              aria-disabled={optDisabled || undefined}
              $active={i === hook.activeIndex}
              $selected={hook.isSelected(item)}
              $disabled={optDisabled}
              onMouseDown={(e) => {
                e.preventDefault();
                if (!optDisabled) hook.select(item);
              }}
              onMouseEnter={() => hook.setActiveIndex(i)}
            >
              {renderOption(item)}
            </OptionItem>
          );
        })
      )}
    </Listbox>
  );
}

export interface AutocompleteOptionProps<T> {
  value: T;
  /** Text shown in the input/chip after selection. Defaults to `getOptionLabel(value)`. */
  label?: string;
  disabled?: boolean;
  children?: React.ReactNode;
}

/** Compound-mode option row. `label` controls input/chip text; `children` is the dropdown row. */
function Option<T>({ value, label, disabled = false, children }: AutocompleteOptionProps<T>) {
  const ctx = useCtx<T>();
  const { hook, listboxId, getOptionKey, getOptionLabel, visibleItems } = ctx;
  const key = getOptionKey(value);
  const index = visibleItems.findIndex((it) => getOptionKey(it) === key);
  const active = index !== -1 && index === hook.activeIndex;
  const selected = hook.isSelected(value);
  const labelText = label ?? getOptionLabel(value);
  return (
    <OptionItem
      id={`${listboxId}-${key}`}
      role="option"
      aria-selected={selected}
      aria-disabled={disabled || undefined}
      $active={active}
      $selected={selected}
      $disabled={disabled}
      onMouseDown={(e) => {
        e.preventDefault();
        if (!disabled) hook.select(value);
      }}
      onMouseEnter={() => index >= 0 && hook.setActiveIndex(index)}
    >
      {children ?? labelText}
    </OptionItem>
  );
}

Autocomplete.Input = Input;
Autocomplete.List = List;
Autocomplete.Empty = Empty;
Autocomplete.Loading = Loading;
Autocomplete.Clear = Clear;
Autocomplete.Chips = Chips;
Autocomplete.Option = Option as <T>(props: AutocompleteOptionProps<T>) => React.JSX.Element;
